import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MediaTab, { mediaFormSchema } from '../components/course/MediaTab';
import ModulesTab from '../components/course/ModulesTab';
import SettingsTab, { settingsFormSchema } from '../components/course/SettingsTab';
import { CourseFormData, Subject } from '../components/course/types';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { ImageCropper } from '../components/ui/image-cropper';
import { useToast } from '../components/ui/use-toast';
import { storage, app } from "../firebase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from './../components/ui/tabs';

import { zodResolver } from '@hookform/resolvers/zod';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import GeneralInfoTab, { courseFormSchema } from '../components/course/GeneralInfoTab';
import { Alert, AlertDescription } from '../components/ui/alert';
import { collection, deleteDoc, doc, FirestoreError, getDoc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Combined schema for all tabs  
const courseSchema = z.object({
  ...courseFormSchema.shape,
  ...mediaFormSchema.shape,
  ...settingsFormSchema.shape,
});

type CourseFormValues = z.infer<typeof courseSchema>;

const CreateCourse = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams(); // Get the course ID from the URL  
  const [courseCategories, setCourseCategories] = useState<any[]>([]); // State to hold course categories  
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [courseId, setCourseId] = useState<string | null>(id || null); // Set courseId from URL  

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Sample subjects with modules  
  const [subjects, setSubjects] = useState<any[]>([
    {
      id: '1',
      subjectName: 'Core Python',
      modules: []
    },
    {
      id: '2',
      subjectName: 'HTML and HTML5',
      modules: []
    }
  ]);

  // Initialize the form with React Hook Form + Zod validation  
  const form = useForm<CourseFormValues>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: '',
      courseDescription: '',
      category: '',
      duration: 0,
      level: '',
      price: 0,
      instructor: '',
      rating: 0,
      status: 'draft',
      thumbnail: null,
      introVideo: null,
      additionalMaterials: [],
      enrollmentLimit: '50',
      certificate: 'default',
    },
    // Validate on change and keep inputs registered when their components unmount (tabs)
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
  });

  // Track form data for non-form components that still need the data  
  const [formData, setFormData] = useState<any>({
    courseName: '',
    courseDescription: '',
    category: '',
    duration: 0,
    level: '',
    price: 0,
    status: 'draft',
    thumbnail: null,
    thumbnailPreview: null,
    introVideo: null,
    instructor: '',
    rating: 0,
  });

  // Update formData when form values change  
  useEffect(() => {
    const subscription = form.watch((value) => {
      setFormData(prev => ({
        ...prev,
        courseName: value.courseName || '',
        courseDescription: value.courseDescription || '',
        category: value.category || '',
        duration: value.duration || 0,
        level: value.level || '',
        price: value.price || 0,
        rating: value?.rating || 0,
        status: value.status || 'draft',
      }));
    });

    const fetchCategories = async () => {
      try {
        setLoading(true); // Start loading state  
        const categoriesCollection = collection(db, 'courseCategories'); // Reference the collection  
        const querySnapshot = await getDocs(categoriesCollection); // Fetch documents from the collection  

        const categoriesData = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get document ID  
          ...doc.data(), // Spread document data  
        }));
        setCourseCategories(categoriesData); // Set the fetched data to state  
      } catch (error) {
        if (error instanceof FirestoreError) {
          console.log(error.message); // Set error message if fetching fails  
        } else {
          console.log("Failed to fetch course categories.");
        }
      } finally {
        setLoading(false); // Stop loading state  
      }
    };

    fetchCategories();
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Load course data when courseId is available  
  useEffect(() => {
    if (courseId) {
      const loadCourseData = async () => {
        try {
          const courseDoc = await getDoc(doc(db, "courses", courseId));
          if (courseDoc.exists()) {
            const courseData = courseDoc.data();
            // Populate the form with existing course data  
            form.reset({
              courseName: courseData.courseName,
              courseDescription: courseData.courseDescription,
              category: courseData.category,
              duration: courseData.duration,
              level: courseData.level,
              price: courseData.price,
              status: courseData.status,
              thumbnail: courseData.thumbnail,
              introVideo: courseData.introVideo,
              additionalMaterials: courseData.additionalMaterials || [],
              enrollmentLimit: courseData.enrollmentLimit,
              certificate: courseData.certificate,
            });

            // Set the form data state as well  
            setFormData({
              courseName: courseData.courseName,
              description: courseData.courseDescription,
              category: courseData.category,
              duration: courseData.duration,
              level: courseData.level,
              price: courseData.price,
              status: courseData.status,
              thumbnail: courseData.thumbnail,
              thumbnailPreview: courseData.thumbnail?.url || null, // Presuming the thumbnail has a url  
              introVideo: courseData.introVideo,
              instructor: courseData?.instructor ?? '',
              additionalMaterials: courseData.additionalMaterials || [],
              enrollmentLimit: courseData.enrollmentLimit,
              certificate: courseData.certificate,
            });
          } else {
            toast({
              title: "Error",
              description: "Course not found.",
              variant: "destructive"
            });
            navigate(`/courses`); // Navigate back if course doesn't exist  
          }
        } catch (error) {
          console.error("Error fetching course data:", error);
          toast({
            title: "Error",
            description: "Failed to load course data.",
            variant: "destructive"
          });
        }
      };

      loadCourseData(); // Call the function to load data  
    }
  }, [courseId, db, form, navigate, toast]); // Dependencies for the useEffect  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value ,type} = e.target;
    setFormData(prev => {
      // 1. Ensure it's a string and trim whitespace
      const stringVal = String(value).trim();

      // 2. Check: Is it non-empty AND a valid number?
      // Regex /^-?\d+(\.\d+)?$/ matches integers and decimals
     // const isNumeric = stringVal !== '' && !isNaN(Number(stringVal));

      const processedValue = type === 'number' ? Number(stringVal) : value;
      form.setValue(name as any, processedValue);   
      return {
        ...prev,
        [name]: processedValue
      };
    });
   
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    form.setValue(name as any, value);
  };

  const handleSubmit = async (values: CourseFormValues) => {
    setLoading(true);

    try {
      // Combine form data with other necessary data  
      const courseData: any = {
        courseName: values.courseName,
        courseDescription: values?.courseDescription,
        category: values.category,
        duration: values.duration,
        level: values.level,
        price: values.price,
        status: values.status,
        thumbnail: { ...values.thumbnail },
        introVideo: values.introVideo,
        additionalMaterials: values.additionalMaterials || [],
        enrollmentLimit: values.enrollmentLimit,
        certificate: values.certificate,
        subjects: [],
      };

      // Create a reference to a new document in the Firestore courses collection with a generated ID  
      const newCourseRef = doc(collection(db, "courses"));

      // Add the generated ID to the course data object  
      const courseDataWithId = {
        id: newCourseRef.id, // Assign the generated ID  
        ...courseData,  // Spread the existing data  
      };

      // Save the course data to Firestore with the generated ID  
      await setDoc(newCourseRef, courseDataWithId);

      toast({
        title: "Course created",
        description: `${courseData.title} has been successfully created`,
      });

      // Navigate to the course details or edit page  
      setTimeout(() => {
        navigate(`/courses/${courseDataWithId.id}`); // Navigate using the generated ID  
      }, 1500);

    } catch (error) {
      console.error("Error creating course:", error);
      toast({
        title: "Error",
        description: "Failed to create the course. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create the thumbnail object with file details  
      const thumbnailObject = {
        fileName: file.name,
        size: file.size,
        url: URL.createObjectURL(file) // Create a temporary URL for the file  
      };

      // Set the thumbnail object in the form  
      form.setValue('thumbnail', thumbnailObject);

      const reader = new FileReader();
      reader.onloadend = () => {
        // Make sure the result is a string  
        const result = typeof reader.result === 'string' ? reader.result : '';

        setTempImage(result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearThumbnail = async () => {
    // Ensure loading state is set  
    setLoading(true);

    try {
      const { thumbnail } = form.getValues(); // Get current form values to access the thumbnail  

      if (thumbnail) {
        // Delete the file from Firebase Storage  
        const fileRef = ref(storage, `files/${thumbnail.fileName}`);
        await deleteObject(fileRef);

        // Delete the file metadata from Firestore  
        const q = query(
          collection(db, "files"),
          where("name", "==", thumbnail.fileName)
        );
        const querySnapshot = await getDocs(q);

        // Use a for...of loop to handle async delete operations  
        for (const doc of querySnapshot.docs) {
          await deleteDoc(doc.ref); // Await the deletion of each document  
        }
      }

      // Clear the thumbnail data from form and internal state  
      setFormData(prev => ({
        ...prev,
        thumbnail: null,
        thumbnailPreview: null
      }));
      form.setValue('thumbnail', null);

    } catch (error) {
      console.error("Error clearing thumbnail:", error);
      toast({
        title: "Error",
        description: "Failed to clear thumbnail. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false); // Hide loader regardless of success or failure  
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    setFormData(prev => ({
      ...prev,
      thumbnail: null, // We will set the thumbnail as an object below  
      thumbnailPreview: croppedImage // This is the preview of the cropped thumbnail  
    }));

    // Create a blob from the cropped image string  
    const response = await fetch(croppedImage);
    const blob = await response.blob();
    const file = new File([blob], `thumbnail.png`, { type: "image/png" }); // Create file from blob  

    // Prepare for uploading thumbnail to storage  
    const storageRef = ref(storage, `files/${file.name}`); // Use a dedicated folder for thumbnails  
    try {
      setLoading(true); // Optionally show a loading indicator  

      // Upload the cropped image file to storage  
      await uploadBytes(storageRef, file);

      // Get download URL after successful upload  
      const url = await getDownloadURL(storageRef);

      // Now we save the thumbnail details in the form data  
      const thumbnailObject = {
        fileName: file.name,
        size: file.size,
        url: url // Set the URL in the thumbnail object  
      };

      // Update the form's thumbnail field with the new object  
      form.setValue('thumbnail', thumbnailObject);

      // Optionally set in state or other variable if you're displaying it elsewhere  
      setFormData(prev => ({
        ...prev,
        thumbnail: thumbnailObject // Updated thumbnail object  
      }));

    } catch (uploadError) {
      console.error(uploadError);
      toast({
        title: "Error",
        description: uploadError.message || "Error uploading thumbnail",
        variant: "destructive"
      });
    } finally {
      setShowCropper(false);
      setTempImage(null);
      setLoading(false); // Hide loader regardless of success or failure  
    }
  };

  // For displaying form errors at the top level  
  const formErrors = form.formState.errors;
  const hasErrors = Object.keys(formErrors).length > 0;
  // Determine if the Create button should be enabled  
  const isFormValid = form.formState.isValid && !loading;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Create Course" subtitle="Add a new course to your curriculum" />
      <div className="mb-0 mt-6 ml-6">
        <Link to={`/courses/`} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Courses
        </Link>
      </div>
      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">
        <Card className="max-w-5xl mx-auto">
          <CardHeader>
            <CardTitle className="text-bytesplash-blue">Create New Course</CardTitle>
            <AnimatePresence>
              {hasErrors && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>
                      Please fix the validation errors before saving.
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>
          </CardHeader>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Tabs
              defaultValue="general"
              value={activeTab}
              onValueChange={(value) => setActiveTab(value)}
            >
              <div className="px-6">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="general">General Information</TabsTrigger>
                  <TabsTrigger value="modules" disabled={!courseId}>
                    Modules
                    {!courseId && (
                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                        Save First
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="media">Media</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="general">
                <GeneralInfoTab
                  formData={formData}
                  handleChange={handleChange}
                  handleSelectChange={handleSelectChange}
                  form={form}
                  categories={courseCategories}
                />
              </TabsContent>

              <TabsContent value="modules" className="px-6">
                {courseId ? (
                  <ModulesTab
                    courseId={courseId}
                    subjects={subjects}
                    onSubjectsChange={setSubjects}
                  />
                ) : (
                  <div className="py-12 text-center">
                    <p className="text-gray-500">
                      Please save the course first to enable module management.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="media">
                <MediaTab
                  formData={formData}
                  handleThumbnailChange={handleThumbnailChange}
                  clearThumbnail={clearThumbnail}
                  form={form}
                />
              </TabsContent>

              <TabsContent value="settings">
                <SettingsTab form={form} />
              </TabsContent>

              <CardFooter className="flex justify-between px-6 py-4">
                <Button variant="outline" type="button" onClick={() => navigate('/courses')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid} // Enable/disable based on form validity  
                >
                  {loading ? 'Creating...' : 'Create Course'}
                </Button>
              </CardFooter>
            </Tabs>
          </form>
        </Card>
      </div>

      {/* Image Cropper */}
      {tempImage && (
        <ImageCropper
          open={showCropper}
          onClose={() => setShowCropper(false)}
          imageSrc={tempImage}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default CreateCourse;  